import { useSuspenseQuery } from '@tanstack/react-query';
import { policyControllerGetPolicyQueryOptions } from 'korint-sdk';
import type { KorintConfig } from 'korint-sdk/react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

type ProductsConfig = NonNullable<KorintConfig['productsConfig']>;

const funnelSegments = new Set(['final', 'mta']);
// Quote emails link to the discovery funnel's first step without the `final` segment.
const legacyDiscoverySteps = new Set(['documents-upload']);

const withDiscoverySegment = (path: string) => {
  const [policyId, branchId, step, ...tail] = path.split('/');
  return legacyDiscoverySteps.has(step ?? '')
    ? [policyId, branchId, 'final', step, ...tail].join('/')
    : path;
};

// Customer.io emails (sent and templated) still link to garantie-plus `/driver/...` routes.
export function LegacyDriverRedirect({ productsConfig }: { productsConfig: ProductsConfig }) {
  const { '*': splat = '' } = useParams();
  const { search } = useLocation();
  const rest = withDiscoverySegment(splat);
  const [policyId = '', , funnel] = rest.split('/');

  if (policyId.startsWith('policy_') && funnelSegments.has(funnel ?? '')) {
    return (
      <ProductFunnelRedirect
        policyId={policyId}
        rest={rest}
        search={search}
        productsConfig={productsConfig}
      />
    );
  }

  return <Navigate to={`/policyholder/${rest}${search}`} replace />;
}

function ProductFunnelRedirect({
  policyId,
  rest,
  search,
  productsConfig,
}: {
  policyId: string;
  rest: string;
  search: string;
  productsConfig: ProductsConfig;
}) {
  const { data: policy } = useSuspenseQuery(policyControllerGetPolicyQueryOptions(policyId));
  const productCustomPath = productsConfig[policy.productId]?.productCustomPath ?? '';

  return <Navigate to={`/policyholder${productCustomPath}/${rest}${search}`} replace />;
}
